/**
 * Order Detail Page
 */
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ListSubheader,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, PersonAdd as PersonAddIcon, SwapHoriz as SwapHorizIcon, Edit as EditIcon } from '@mui/icons-material';
import { useState, useMemo, useEffect } from 'react';
import { PageHeader, StatusChip, PermissionGuard } from '../../components/common';
import {
  useGetOrderQuery,
  useUpdateOrderStatusMutation,
  useAssignProviderMutation,
  useReassignProviderMutation,
  useUpdateOrderPatientLocationMutation,
} from '../../store/api/ordersApi';
import { useGetSpecialistsQuery } from '../../store/api/specialistsApi';
import { useGetConsultantsQuery } from '../../store/api/consultantsApi';
import { OrderStatus } from '../../types/order';
import {
  getAllowedNextOrderStatuses,
  splitAllowedStatusesForSelect,
  formatOrderStatusUpdateError,
  orderTransitionRequiresPlatformFee,
  normalizePlatformFeeInput,
  normalizeServicePriceInput,
} from '../../utils/orderStatusTransitions';
import moment from 'moment';
import toast from 'react-hot-toast';
import OrderReviewPanel from '../../components/orders/OrderReviewPanel';
import OrderTimeline from '../../components/orders/OrderTimeline';
import { useTranslation } from 'react-i18next';
import { formatCurrency as formatCurrencyLocalized } from '../../utils/format';
import { usePermissions } from '../../hooks/usePermissions';
import OrderResultsPanel from '../../components/orders/OrderResultsPanel';

const OrderDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canAssignProvider = hasPermission('assign_order_provider');
  const { data, isLoading, error } = useGetOrderQuery(Number(id));
  const [updateStatus, { isLoading: isUpdating }] = useUpdateOrderStatusMutation();
  const [assignProvider, { isLoading: isAssigning }] = useAssignProviderMutation();
  const [reassignProvider, { isLoading: isReassigning }] = useReassignProviderMutation();
  const [updatePatientLocation, { isLoading: isSavingPatientLocation }] =
    useUpdateOrderPatientLocationMutation();

  // Provider lists only when user may assign (avoids 403 noise)
  const { data: specialistsData } = useGetSpecialistsQuery({}, { skip: !canAssignProvider });
  const { data: consultantsData } = useGetConsultantsQuery({}, { skip: !canAssignProvider });

  const [statusDialog, setStatusDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [reassignDialog, setReassignDialog] = useState(false);
  const [newStatus, setNewStatus] = useState<OrderStatus | ''>('');
  const [adminNotes, setAdminNotes] = useState('');
  const [providerType, setProviderType] = useState<'SPECIALIST' | 'CONSULTANT'>('SPECIALIST');
  const [providerId, setProviderId] = useState<number | ''>('');
  const [assignNotes, setAssignNotes] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [statusPlatformFee, setStatusPlatformFee] = useState('0.00');
  const [assignPlatformFee, setAssignPlatformFee] = useState('0.00');
  const [reassignPlatformFee, setReassignPlatformFee] = useState('0.00');
  const [statusServicePrice, setStatusServicePrice] = useState('');
  const [assignServicePrice, setAssignServicePrice] = useState('');
  const [reassignServicePrice, setReassignServicePrice] = useState('');

  const [patientLocationDialog, setPatientLocationDialog] = useState(false);
  const [patientForm, setPatientForm] = useState({
    patient_name: '',
    patient_age: '',
    patient_gender: 'M' as 'M' | 'F',
    patient_phone: '',
    health_conditions: '',
    allergies: '',
    current_medications: '',
    special_notes: '',
    full_address: '',
    city: '',
    area: '',
    latitude: '',
    longitude: '',
  });

  const order = data?.data;

  const canEditPatientAndLocation =
    order &&
    !['COMPLETED', 'CANCELLED', 'REJECTED', 'REFUNDED'].includes(order.status);

  useEffect(() => {
    if (!patientLocationDialog || !order) return;
    setPatientForm({
      patient_name: order.patient_name,
      patient_age: String(order.patient_age),
      patient_gender: order.patient_gender === 'F' ? 'F' : 'M',
      patient_phone: order.patient_phone,
      health_conditions: order.health_conditions || '',
      allergies: order.allergies || '',
      current_medications: order.current_medications || '',
      special_notes: order.special_notes || '',
      full_address: order.location?.full_address || '',
      city: order.location?.city || '',
      area: order.location?.area || '',
      latitude: order.location?.latitude ?? '',
      longitude: order.location?.longitude ?? '',
    });
  }, [patientLocationDialog, order]);

  const allowedNextStatuses = useMemo(
    () => (order ? getAllowedNextOrderStatuses(order.status) : []),
    [order?.status]
  );

  const { forward: forwardStatuses, terminal: terminalStatuses } = useMemo(
    () => splitAllowedStatusesForSelect(allowedNextStatuses),
    [allowedNextStatuses]
  );

  const statusConfirmRequiresFee = Boolean(
    order && newStatus && orderTransitionRequiresPlatformFee(order.status, newStatus)
  );

  // Home Doctor Visit order with no preset price: admin must set the visit price when confirming.
  const orderNeedsPrice = Boolean(order?.requires_price_input);
  const statusConfirmRequiresPrice = orderNeedsPrice && newStatus === 'CONFIRMED';
  const confirmFromPendingRequiresPrice = orderNeedsPrice && order?.status === 'PENDING';

  // Format currency (locale-aware)
  const formatCurrency = (amount: string | number): string => formatCurrencyLocalized(amount);

  const handleStatusUpdate = async () => {
    if (!newStatus || !order) return;

    let platform_fee: string | undefined;
    if (orderTransitionRequiresPlatformFee(order.status, newStatus)) {
      const normalized = normalizePlatformFeeInput(statusPlatformFee);
      if (!normalized) {
        toast.error(t('serviceOrders.toast.invalidDeliveryFee'));
        return;
      }
      platform_fee = normalized;
    }

    let service_price: string | undefined;
    if (orderNeedsPrice && newStatus === 'CONFIRMED') {
      const normalizedPrice = normalizeServicePriceInput(statusServicePrice);
      if (!normalizedPrice) {
        toast.error(t('serviceOrders.toast.invalidVisitPrice'));
        return;
      }
      service_price = normalizedPrice;
    }

    try {
      await updateStatus({
        order_id: order.id,
        status: newStatus,
        admin_notes: adminNotes || undefined,
        ...(platform_fee !== undefined && { platform_fee }),
        ...(service_price !== undefined && { service_price }),
      }).unwrap();
      toast.success(t('serviceOrders.toast.statusUpdated'));
      setStatusDialog(false);
      setNewStatus('');
      setAdminNotes('');
      setStatusPlatformFee('0.00');
      setStatusServicePrice('');
    } catch (err: unknown) {
      toast.error(formatOrderStatusUpdateError(err));
    }
  };

  const handleAssignProvider = async () => {
    if (!providerId || !order) return;

    let platform_fee: string | undefined;
    if (order.status === 'PENDING') {
      const normalized = normalizePlatformFeeInput(assignPlatformFee);
      if (!normalized) {
        toast.error(t('serviceOrders.toast.invalidDeliveryFee'));
        return;
      }
      platform_fee = normalized;
    }

    let service_price: string | undefined;
    if (orderNeedsPrice && order.status === 'PENDING') {
      const normalizedPrice = normalizeServicePriceInput(assignServicePrice);
      if (!normalizedPrice) {
        toast.error(t('serviceOrders.toast.invalidVisitPrice'));
        return;
      }
      service_price = normalizedPrice;
    }

    try {
      await assignProvider({
        order_id: order.id,
        provider_type: providerType,
        provider_id: Number(providerId),
        admin_notes: assignNotes || undefined,
        ...(platform_fee !== undefined && { platform_fee }),
        ...(service_price !== undefined && { service_price }),
      }).unwrap();
      toast.success(t('serviceOrders.toast.providerAssigned'));
      setAssignDialog(false);
      setProviderType('SPECIALIST');
      setProviderId('');
      setAssignNotes('');
      setAssignPlatformFee('0.00');
      setAssignServicePrice('');
    } catch (err: any) {
      const fieldErr = err?.data?.errors?.service_price ?? err?.data?.errors?.platform_fee;
      const fieldMsg = Array.isArray(fieldErr) ? fieldErr[0] : fieldErr;
      toast.error(fieldMsg || err?.data?.message || t('serviceOrders.toast.assignFailed'));
    }
  };

  const handleReassignProvider = async () => {
    if (!providerId || !order) return;

    let platform_fee: string | undefined;
    if (order.status === 'PENDING') {
      const normalized = normalizePlatformFeeInput(reassignPlatformFee);
      if (!normalized) {
        toast.error(t('serviceOrders.toast.invalidDeliveryFee'));
        return;
      }
      platform_fee = normalized;
    }

    let service_price: string | undefined;
    if (orderNeedsPrice && order.status === 'PENDING') {
      const normalizedPrice = normalizeServicePriceInput(reassignServicePrice);
      if (!normalizedPrice) {
        toast.error(t('serviceOrders.toast.invalidVisitPrice'));
        return;
      }
      service_price = normalizedPrice;
    }

    try {
      await reassignProvider({
        order_id: order.id,
        provider_type: providerType,
        provider_id: Number(providerId),
        reason: reassignReason || undefined,
        ...(platform_fee !== undefined && { platform_fee }),
        ...(service_price !== undefined && { service_price }),
      }).unwrap();
      toast.success(t('serviceOrders.toast.providerReassigned'));
      setReassignDialog(false);
      setProviderType('SPECIALIST');
      setProviderId('');
      setReassignReason('');
      setReassignPlatformFee('0.00');
      setReassignServicePrice('');
    } catch (err: any) {
      const fieldErr = err?.data?.errors?.service_price ?? err?.data?.errors?.platform_fee;
      const fieldMsg = Array.isArray(fieldErr) ? fieldErr[0] : fieldErr;
      toast.error(fieldMsg || err?.data?.message || t('serviceOrders.toast.reassignFailed'));
    }
  };

  const handleSavePatientLocation = async () => {
    if (!order) return;
    const name = patientForm.patient_name.trim();
    const phone = patientForm.patient_phone.trim();
    if (!name) {
      toast.error(t('serviceOrders.toast.patientNameRequired'));
      return;
    }
    if (!phone) {
      toast.error(t('serviceOrders.toast.patientPhoneRequired'));
      return;
    }
    const age = Number.parseInt(patientForm.patient_age, 10);
    if (!Number.isFinite(age) || age < 1) {
      toast.error(t('serviceOrders.toast.invalidAge'));
      return;
    }

    const latStr = String(patientForm.latitude ?? '').trim();
    const lngStr = String(patientForm.longitude ?? '').trim();
    if ((latStr && !lngStr) || (!latStr && lngStr)) {
      toast.error(t('serviceOrders.toast.latLngBoth'));
      return;
    }

    try {
      await updatePatientLocation({
        order_id: order.id,
        patient_name: name,
        patient_age: age,
        patient_gender: patientForm.patient_gender,
        patient_phone: phone,
        health_conditions: patientForm.health_conditions.trim(),
        allergies: patientForm.allergies.trim(),
        current_medications: patientForm.current_medications.trim(),
        special_notes: patientForm.special_notes.trim(),
        full_address: patientForm.full_address.trim(),
        city: patientForm.city.trim(),
        area: patientForm.area.trim(),
        ...(latStr && lngStr ? { latitude: latStr, longitude: lngStr } : {}),
      }).unwrap();
      toast.success(t('serviceOrders.toast.patientLocationUpdated'));
      setPatientLocationDialog(false);
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        (typeof err?.data?.errors === 'object' && err?.data?.errors?.detail?.[0]) ||
        (typeof err?.data?.errors === 'object' && err?.data?.errors?.detail) ||
        t('serviceOrders.toast.patientLocationFailed');
      toast.error(typeof msg === 'string' ? msg : t('serviceOrders.toast.patientLocationFailed'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{t('serviceOrders.errors.notFound')}</Typography>
        <Button onClick={() => navigate('/orders')} sx={{ mt: 2 }}>
          {t('serviceOrders.buttons.backToOrders')}
        </Button>
      </Box>
    );
  }

  const availableProviders = providerType === 'SPECIALIST'
    ? specialistsData?.results || []
    : consultantsData?.results || [];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('serviceOrders.detail.title', { number: order.order_number })}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.orders'), path: '/orders' },
          { label: order.order_number },
        ]}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>
          {t('serviceOrders.buttons.backToOrders')}
        </Button>
        <PermissionGuard permissions={['edit_orders']}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            disabled={!canEditPatientAndLocation}
            title={
              canEditPatientAndLocation
                ? t('serviceOrders.detail.editTitleEnabled')
                : t('serviceOrders.detail.editTitleDisabled')
            }
            onClick={() => setPatientLocationDialog(true)}
          >
            {t('serviceOrders.buttons.editOrder')}
          </Button>
        </PermissionGuard>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          {/* Order Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('serviceOrders.detail.orderDetails')}
                </Typography>
                <StatusChip status={order.status} />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                {order.service && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.service')}</Typography>
                    <Typography variant="body1">{order.service.name}</Typography>
                  </Box>
                )}
                {order.subservice && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.subservice')}</Typography>
                    <Typography variant="body1">{order.subservice.name}</Typography>
                  </Box>
                )}
                {!order.subservice && order.internal_subservice && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.item')}</Typography>
                    <Typography variant="body1">{order.internal_subservice.name}</Typography>
                  </Box>
                )}
                {order.order_type && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.orderType')}</Typography>
                    <Typography variant="body1">
                      <Chip label={order.order_type} size="small" />
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.scheduledDate')}</Typography>
                  <Typography variant="body1">
                    {moment(order.scheduled_date).format('MMMM DD, YYYY')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.time')}</Typography>
                  <Typography variant="body1">
                    {moment(order.scheduled_time_from, 'HH:mm:ss').format('h:mm A')} - {moment(order.scheduled_time_to, 'HH:mm:ss').format('h:mm A')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.servicePrice')}</Typography>
                  <Typography variant="body1">{formatCurrency(order.service_price)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.deliveryFee')}</Typography>
                  <Typography variant="body1">{formatCurrency(order.platform_fee)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.totalPatientPays')}</Typography>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    {formatCurrency(order.total_amount)}
                  </Typography>
                </Box>
              </Box>

              {/* Special Request Preferences - Only show for special requests */}
              {order.is_special_request && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {t('serviceOrders.detail.specialRequestPreferences')}
                    </Typography>
                    <Chip
                      label={t('serviceOrders.detail.specialRequest')}
                      size="small"
                      color="secondary"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(3, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    {order.preferred_gender && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.preferredGender')}</Typography>
                        <Typography variant="body1">
                          {order.preferred_gender === 'NO_PREFERENCE' ? t('serviceOrders.detail.noPreference') :
                           order.preferred_gender.charAt(0) + order.preferred_gender.slice(1).toLowerCase()}
                        </Typography>
                      </Box>
                    )}
                    {order.preferred_age_range && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.preferredAgeRange')}</Typography>
                        <Typography variant="body1">
                          {order.preferred_age_range === 'NO_PREFERENCE' ? t('serviceOrders.detail.noPreference') :
                           order.preferred_age_range}
                        </Typography>
                      </Box>
                    )}
                    {order.preferred_language && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.preferredLanguage')}</Typography>
                        <Typography variant="body1">
                          {order.preferred_language === 'NO_PREFERENCE' ? t('serviceOrders.detail.noPreference') :
                           order.preferred_language === 'BOTH' ? t('serviceOrders.detail.arabicEnglish') :
                           order.preferred_language.charAt(0) + order.preferred_language.slice(1).toLowerCase()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Patient Info */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('serviceOrders.detail.patientInfo')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.name')}</Typography>
                  <Typography variant="body1">{order.patient_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.phone')}</Typography>
                  <Typography variant="body1">{order.patient_phone}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.age')}</Typography>
                  <Typography variant="body1">{order.patient_age} {t('serviceOrders.detail.years')}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.gender')}</Typography>
                  <Typography variant="body1">{order.patient_gender === 'M' ? t('serviceOrders.detail.male') : t('serviceOrders.detail.female')}</Typography>
                </Box>
                {order.health_conditions && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.healthConditions')}</Typography>
                    <Typography variant="body1">{order.health_conditions}</Typography>
                  </Box>
                )}
                {order.allergies && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.allergies')}</Typography>
                    <Typography variant="body1">{order.allergies}</Typography>
                  </Box>
                )}
                {order.current_medications && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.currentMedications')}</Typography>
                    <Typography variant="body1">{order.current_medications}</Typography>
                  </Box>
                )}
                {order.special_notes && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.specialNotes')}</Typography>
                    <Typography variant="body1">{order.special_notes}</Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Location */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('serviceOrders.detail.location')}
              </Typography>
              <Typography variant="body1">{order.location.full_address}</Typography>
              <Typography variant="body2" color="text.secondary">
                {order.location.city}, {order.location.area}
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* User Info */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('serviceOrders.detail.customerInfo')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.name')}</Typography>
                  <Typography variant="body1">{order.user.full_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.email')}</Typography>
                  <Typography variant="body1">{order.user.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.phone')}</Typography>
                  <Typography variant="body1">{order.user.phone_number}</Typography>
                </Box>
              </Box>

              {order.status === 'CONFIRMED' && !order.payment && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Alert severity="info" sx={{ mb: 0 }}>
                    {t('serviceOrders.detail.awaitingPayment')}
                  </Alert>
                </>
              )}

              {order.payment && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('serviceOrders.detail.paymentInfo')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.paymentMethod')}</Typography>
                      <Typography variant="body1">{order.payment.payment_method}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.paymentStatus')}</Typography>
                      <Chip
                        label={order.payment.status}
                        size="small"
                        color={order.payment.status === 'COMPLETED' ? 'success' : 'warning'}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.amount')}</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatCurrency(order.payment.amount)}
                      </Typography>
                    </Box>
                    {order.payment.paid_at && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('serviceOrders.detail.paidAt')}</Typography>
                        <Typography variant="body1">
                          {moment(order.payment.paid_at).format('MMMM DD, YYYY HH:mm')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          {/* Actions Card */}
          <Card sx={{ mb: 3 }} id="order-actions-card">
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('common.actions')}
              </Typography>

              {orderNeedsPrice && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('serviceOrders.detail.noPresetPriceAlert')}
                </Alert>
              )}

              {/* Update Status — requires update_order_status */}
              <PermissionGuard permissions={['update_order_status']}>
                {allowedNextStatuses.length > 0 ? (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      setNewStatus('');
                      setAdminNotes('');
                      setStatusPlatformFee(
                        Number.parseFloat(order.platform_fee) >= 0
                          ? Number.parseFloat(order.platform_fee).toFixed(2)
                          : '0.00'
                      );
                      setStatusServicePrice('');
                      setStatusDialog(true);
                    }}
                    sx={{ mb: 2 }}
                  >
                    {t('serviceOrders.buttons.updateStatus')}
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('serviceOrders.detail.noStatusChanges')}
                  </Typography>
                )}
              </PermissionGuard>

              {/* Assign/Reassign — requires assign_order_provider */}
              <PermissionGuard permissions={['assign_order_provider']}>
                {!order.assigned_provider ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => {
                      setAssignPlatformFee(
                        Number.parseFloat(order.platform_fee) >= 0
                          ? Number.parseFloat(order.platform_fee).toFixed(2)
                          : '0.00'
                      );
                      setAssignServicePrice('');
                      setAssignDialog(true);
                    }}
                  >
                    {t('serviceOrders.buttons.assignProvider')}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => {
                      setReassignPlatformFee(
                        Number.parseFloat(order.platform_fee) >= 0
                          ? Number.parseFloat(order.platform_fee).toFixed(2)
                          : '0.00'
                      );
                      setReassignServicePrice('');
                      setReassignDialog(true);
                    }}
                    sx={{ mt: 1 }}
                  >
                    {t('serviceOrders.buttons.reassignProvider')}
                  </Button>
                )}
              </PermissionGuard>
            </CardContent>
          </Card>

          {/* Provider Card */}
          {order.assigned_provider ? (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('serviceOrders.detail.assignedProvider')}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {order.assigned_provider.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {order.assigned_provider.type}
                </Typography>
                {order.assigned_provider.specialization && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {order.assigned_provider.specialization}
                  </Typography>
                )}
                {order.assigned_provider.phone && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {order.assigned_provider.phone}
                  </Typography>
                )}
                {order.assigned_provider.email && (
                  <Typography variant="body2" color="text.secondary">
                    {order.assigned_provider.email}
                  </Typography>
                )}
                {order.assigned_provider.rating && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('serviceOrders.detail.rating')}: {order.assigned_provider.rating}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('serviceOrders.detail.noProviderAssigned')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <OrderResultsPanel orderId={order.id} />
      </Box>

      <OrderTimeline orderId={order.id} />

      <OrderReviewPanel order={order} />

      {/* Edit patient & location (edit_orders) */}
      <Dialog
        open={patientLocationDialog}
        onClose={() => setPatientLocationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('serviceOrders.dialog.editPatientLocationTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('serviceOrders.dialog.editPatientLocationNote')}
          </Typography>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {t('serviceOrders.dialog.patientSection')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label={t('serviceOrders.form.name')}
              value={patientForm.patient_name}
              onChange={(e) => setPatientForm((f) => ({ ...f, patient_name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label={t('serviceOrders.form.phone')}
              value={patientForm.patient_phone}
              onChange={(e) => setPatientForm((f) => ({ ...f, patient_phone: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label={t('serviceOrders.form.age')}
              type="number"
              inputProps={{ min: 1 }}
              value={patientForm.patient_age}
              onChange={(e) => setPatientForm((f) => ({ ...f, patient_age: e.target.value }))}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t('serviceOrders.form.gender')}</InputLabel>
              <Select
                label={t('serviceOrders.form.gender')}
                value={patientForm.patient_gender}
                onChange={(e) =>
                  setPatientForm((f) => ({
                    ...f,
                    patient_gender: e.target.value as 'M' | 'F',
                  }))
                }
              >
                <MenuItem value="M">{t('serviceOrders.detail.male')}</MenuItem>
                <MenuItem value="F">{t('serviceOrders.detail.female')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('serviceOrders.form.healthConditions')}
              value={patientForm.health_conditions}
              onChange={(e) => setPatientForm((f) => ({ ...f, health_conditions: e.target.value }))}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label={t('serviceOrders.form.allergies')}
              value={patientForm.allergies}
              onChange={(e) => setPatientForm((f) => ({ ...f, allergies: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t('serviceOrders.form.currentMedications')}
              value={patientForm.current_medications}
              onChange={(e) => setPatientForm((f) => ({ ...f, current_medications: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t('serviceOrders.form.specialNotes')}
              value={patientForm.special_notes}
              onChange={(e) => setPatientForm((f) => ({ ...f, special_notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {t('serviceOrders.detail.location')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <TextField
              label={t('serviceOrders.form.fullAddress')}
              value={patientForm.full_address}
              onChange={(e) => setPatientForm((f) => ({ ...f, full_address: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              label={t('serviceOrders.form.cityGovernorate')}
              value={patientForm.city}
              onChange={(e) => setPatientForm((f) => ({ ...f, city: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t('serviceOrders.form.area')}
              value={patientForm.area}
              onChange={(e) => setPatientForm((f) => ({ ...f, area: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t('serviceOrders.form.latitude')}
              value={patientForm.latitude}
              onChange={(e) => setPatientForm((f) => ({ ...f, latitude: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t('serviceOrders.form.longitude')}
              value={patientForm.longitude}
              onChange={(e) => setPatientForm((f) => ({ ...f, longitude: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPatientLocationDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSavePatientLocation}
            disabled={isSavingPatientLocation}
          >
            {isSavingPatientLocation ? t('serviceOrders.buttons.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialog}
        onClose={() => {
          setStatusDialog(false);
          setNewStatus('');
          setStatusPlatformFee('0.00');
          setStatusServicePrice('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('serviceOrders.dialog.updateStatusTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {order && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('serviceOrders.dialog.currentStatus')} <strong>{t(`serviceOrders.status.${order.status}`)}</strong>. {t('serviceOrders.dialog.forwardOnlyNote')}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('serviceOrders.dialog.newStatus')}</InputLabel>
            <Select
              value={newStatus}
              label={t('serviceOrders.dialog.newStatus')}
              onChange={(e) => setNewStatus(e.target.value as OrderStatus)}
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
            >
              {forwardStatuses.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>
                  {terminalStatuses.length > 0 ? t('serviceOrders.dialog.moveForward') : t('serviceOrders.dialog.nextStatus')}
                </ListSubheader>
              )}
              {forwardStatuses.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(`serviceOrders.status.${key}`)}
                </MenuItem>
              ))}
              {terminalStatuses.length > 0 && (
                <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>
                  {t('serviceOrders.dialog.cancelOrReject')}
                </ListSubheader>
              )}
              {terminalStatuses.map((key) => (
                <MenuItem key={key} value={key}>
                  {t(`serviceOrders.status.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {statusConfirmRequiresPrice && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label={t('serviceOrders.dialog.visitPriceLabel')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={statusServicePrice}
              onChange={(e) => setStatusServicePrice(e.target.value)}
              helperText={t('serviceOrders.dialog.visitPriceHelper')}
            />
          )}
          {statusConfirmRequiresFee && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label={t('serviceOrders.dialog.deliveryFeeLabel')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={statusPlatformFee}
              onChange={(e) => setStatusPlatformFee(e.target.value)}
              helperText={t('serviceOrders.dialog.deliveryFeeHelper')}
            />
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('serviceOrders.dialog.adminNotes')}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStatusDialog(false);
              setNewStatus('');
              setStatusPlatformFee('0.00');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdate}
            disabled={
              !newStatus ||
              isUpdating ||
              (statusConfirmRequiresFee && !normalizePlatformFeeInput(statusPlatformFee)) ||
              (statusConfirmRequiresPrice && !normalizeServicePriceInput(statusServicePrice))
            }
          >
            {isUpdating ? t('serviceOrders.buttons.updating') : t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Provider Dialog */}
      <Dialog
        open={assignDialog}
        onClose={() => {
          setAssignDialog(false);
          setAssignPlatformFee('0.00');
          setAssignServicePrice('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('serviceOrders.buttons.assignProvider')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {order.status === 'PENDING' && (
            <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
              {t('serviceOrders.dialog.assignConfirmNote')}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('serviceOrders.dialog.providerType')}</InputLabel>
            <Select
              value={providerType}
              label={t('serviceOrders.dialog.providerType')}
              onChange={(e) => {
                setProviderType(e.target.value as 'SPECIALIST' | 'CONSULTANT');
                setProviderId('');
              }}
            >
              <MenuItem value="SPECIALIST">{t('serviceOrders.providerType.specialist')}</MenuItem>
              <MenuItem value="CONSULTANT">{t('serviceOrders.providerType.consultant')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('serviceOrders.dialog.providerLabel')}</InputLabel>
            <Select
              value={providerId}
              label={t('serviceOrders.dialog.providerLabel')}
              onChange={(e) => setProviderId(Number(e.target.value))}
            >
              {availableProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.full_name} {provider.specialization ? `- ${provider.specialization}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {availableProviders.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('serviceOrders.dialog.noProvidersAvailable', { type: t(`serviceOrders.providerType.${providerType.toLowerCase()}`) })}
            </Alert>
          )}
          {confirmFromPendingRequiresPrice && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label={t('serviceOrders.dialog.visitPriceLabel')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={assignServicePrice}
              onChange={(e) => setAssignServicePrice(e.target.value)}
              helperText={t('serviceOrders.dialog.visitPriceHelper')}
            />
          )}
          {order.status === 'PENDING' && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label={t('serviceOrders.dialog.deliveryFeeLabel')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={assignPlatformFee}
              onChange={(e) => setAssignPlatformFee(e.target.value)}
            />
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('serviceOrders.dialog.adminNotes')}
            value={assignNotes}
            onChange={(e) => setAssignNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAssignDialog(false);
              setAssignPlatformFee('0.00');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignProvider}
            disabled={
              !providerId ||
              isAssigning ||
              availableProviders.length === 0 ||
              (order.status === 'PENDING' && !normalizePlatformFeeInput(assignPlatformFee)) ||
              (confirmFromPendingRequiresPrice && !normalizeServicePriceInput(assignServicePrice))
            }
          >
            {isAssigning ? t('serviceOrders.buttons.assigning') : t('serviceOrders.buttons.assign')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Provider Dialog */}
      <Dialog
        open={reassignDialog}
        onClose={() => {
          setReassignDialog(false);
          setReassignPlatformFee('0.00');
          setReassignServicePrice('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('serviceOrders.buttons.reassignProvider')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            {t('serviceOrders.dialog.currentProvider', { name: order.assigned_provider?.name })}
          </Alert>
          {order.status === 'PENDING' && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('serviceOrders.dialog.reassignPendingNote')}
              </Alert>
              {confirmFromPendingRequiresPrice && (
                <TextField
                  fullWidth
                  sx={{ mb: 2 }}
                  label={t('serviceOrders.dialog.visitPriceLabel')}
                  type="number"
                  inputProps={{ min: 0, step: '0.01' }}
                  value={reassignServicePrice}
                  onChange={(e) => setReassignServicePrice(e.target.value)}
                  helperText={t('serviceOrders.dialog.visitPriceHelper')}
                />
              )}
              <TextField
                fullWidth
                sx={{ mb: 2 }}
                label={t('serviceOrders.dialog.deliveryFeeLabel')}
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={reassignPlatformFee}
                onChange={(e) => setReassignPlatformFee(e.target.value)}
              />
            </>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('serviceOrders.dialog.providerType')}</InputLabel>
            <Select
              value={providerType}
              label={t('serviceOrders.dialog.providerType')}
              onChange={(e) => {
                setProviderType(e.target.value as 'SPECIALIST' | 'CONSULTANT');
                setProviderId('');
              }}
            >
              <MenuItem value="SPECIALIST">{t('serviceOrders.providerType.specialist')}</MenuItem>
              <MenuItem value="CONSULTANT">{t('serviceOrders.providerType.consultant')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('serviceOrders.dialog.newProviderLabel')}</InputLabel>
            <Select
              value={providerId}
              label={t('serviceOrders.dialog.newProviderLabel')}
              onChange={(e) => setProviderId(Number(e.target.value))}
            >
              {availableProviders
                .filter((p) => p.id !== order.assigned_provider?.id)
                .map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.full_name} {provider.specialization ? `- ${provider.specialization}` : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {availableProviders.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('serviceOrders.dialog.noProvidersAvailable', { type: t(`serviceOrders.providerType.${providerType.toLowerCase()}`) })}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('serviceOrders.dialog.reasonLabel')}
            value={reassignReason}
            onChange={(e) => setReassignReason(e.target.value)}
            placeholder={t('serviceOrders.dialog.reasonPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReassignDialog(false);
              setReassignPlatformFee('0.00');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleReassignProvider}
            disabled={
              !providerId ||
              isReassigning ||
              availableProviders.length === 0 ||
              (order.status === 'PENDING' && !normalizePlatformFeeInput(reassignPlatformFee)) ||
              (confirmFromPendingRequiresPrice && !normalizeServicePriceInput(reassignServicePrice))
            }
          >
            {isReassigning ? t('serviceOrders.buttons.reassigning') : t('serviceOrders.buttons.reassign')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderDetailPage;
